/**
 * TurtleNode - Stateful 3D turtle graphics
 *
 * Two modes:
 * - Batch: set sequence param → computes all polylines immediately
 * - Stepped: receive symbol events one at a time (e.g. from PatternPlayer)
 *
 * Symbol mappings are configurable via custom params (Symbols group).
 * Users can remap symbols or add new ones via the inspector.
 *
 * Outputs polylines: array of {x, y, z}[] line segments.
 */

import { BaseNode } from '@nodes/BaseNode';
import { events, internals, params, triggers } from '@nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { Dependencies } from '@nodes/Dependencies';
import type { Payload } from '@graph/Payload';
import type { IEndpoint } from '@runtime/IEndpoint';
import type { Preset } from '@/nodes/PresetManager';
import { colorToHex } from '@plugins/threejs/colorUtils';
import { PolylineRenderer } from './PolylineRenderer';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface Vec3 { x: number; y: number; z: number }
interface Quat { x: number; y: number; z: number; w: number }

interface TurtleState {
  position: Vec3;
  rotation: Quat;
  penDown: boolean;
}

export interface StyledPolyline {
  points: Vec3[];
  color?: string;
  fill?: string;
  lineWidth?: number;
  instanceIndex?: number;
}
export type Polylines = StyledPolyline[];

// ─────────────────────────────────────────────────────────────
// Vector / Quaternion math
// ─────────────────────────────────────────────────────────────

const FORWARD: Vec3 = { x: 0, y: 1, z: 0 };
const UP: Vec3 = { x: 0, y: 0, z: 1 };
const RIGHT: Vec3 = { x: 1, y: 0, z: 0 };

function addVec3(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

function scaleVec3(v: Vec3, s: number): Vec3 {
  return { x: v.x * s, y: v.y * s, z: v.z * s };
}

function quatFromAxisAngle(axis: Vec3, angle: number): Quat {
  const half = angle / 2;
  const s = Math.sin(half);
  return { x: axis.x * s, y: axis.y * s, z: axis.z * s, w: Math.cos(half) };
}

function quatMultiply(a: Quat, b: Quat): Quat {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z
  };
}

function rotateVec3(v: Vec3, q: Quat): Vec3 {
  const tx = 2 * (q.y * v.z - q.z * v.y);
  const ty = 2 * (q.z * v.x - q.x * v.z);
  const tz = 2 * (q.x * v.y - q.y * v.x);
  return {
    x: v.x + q.w * tx + (q.y * tz - q.z * ty),
    y: v.y + q.w * ty + (q.z * tx - q.x * tz),
    z: v.z + q.w * tz + (q.x * ty - q.y * tx)
  };
}

function cloneState(s: TurtleState): TurtleState {
  return { position: { ...s.position }, rotation: { ...s.rotation }, penDown: s.penDown };
}

// ─────────────────────────────────────────────────────────────
// Symbol mapping
// ─────────────────────────────────────────────────────────────

const TURTLE_ACTIONS = ['forward', 'yaw+', 'yaw-', 'pitch+', 'pitch-', 'roll+', 'roll-', 'reverse', 'push', 'pop', 'noop'] as const;
const TURTLE_LABELS = ['Forward', 'Yaw +', 'Yaw -', 'Pitch +', 'Pitch -', 'Roll +', 'Roll -', 'Reverse', 'Push', 'Pop', 'No Action'];

const DEFAULT_SYMBOLS: Record<string, string> = {
  'F': 'forward', 'G': 'forward', 'A': 'forward', 'B': 'forward',
  '+': 'yaw+', '-': 'yaw-', '^': 'pitch+', '&': 'pitch-',
  '<': 'roll-', '>': 'roll+', '|': 'reverse', '[': 'push', ']': 'pop',
};

const TURTLE_PRESETS: Preset[] = [
  {
    name: 'Standard',
    params: {
      'F': 'forward', 'G': 'forward', 'A': 'forward', 'B': 'forward',
      '+': 'yaw+', '-': 'yaw-', '^': 'pitch+', '&': 'pitch-',
      '<': 'roll-', '>': 'roll+', '|': 'reverse', '[': 'push', ']': 'pop',
    }
  },
  {
    name: '2D',
    params: {
      'F': 'forward', 'G': 'forward', 'A': 'forward', 'B': 'forward',
      '+': 'yaw+', '-': 'yaw-', '[': 'push', ']': 'pop',
    }
  },
];

// ─────────────────────────────────────────────────────────────
// Symbol interpretation (data-driven)
// ─────────────────────────────────────────────────────────────

function executeSymbol(
  symbol: string,
  state: TurtleState,
  stack: TurtleState[],
  angleRad: number,
  stepSize: number,
  polylines: Vec3[][],
  currentLine: Vec3[],
  symbolMap: Record<string, string>
): Vec3[] {
  const action = symbolMap[symbol] ?? 'noop';
  switch (action) {
    case 'forward': {
      const dir = rotateVec3(FORWARD, state.rotation);
      state.position = addVec3(state.position, scaleVec3(dir, stepSize));
      if (state.penDown) {
        currentLine.push({ ...state.position });
      } else {
        currentLine = [{ ...state.position }];
        polylines.push(currentLine);
      }
      break;
    }
    case 'yaw+':
      state.rotation = quatMultiply(state.rotation, quatFromAxisAngle(UP, angleRad));
      break;
    case 'yaw-':
      state.rotation = quatMultiply(state.rotation, quatFromAxisAngle(UP, -angleRad));
      break;
    case 'pitch+':
      state.rotation = quatMultiply(state.rotation, quatFromAxisAngle(RIGHT, angleRad));
      break;
    case 'pitch-':
      state.rotation = quatMultiply(state.rotation, quatFromAxisAngle(RIGHT, -angleRad));
      break;
    case 'roll+':
      state.rotation = quatMultiply(state.rotation, quatFromAxisAngle(FORWARD, angleRad));
      break;
    case 'roll-':
      state.rotation = quatMultiply(state.rotation, quatFromAxisAngle(FORWARD, -angleRad));
      break;
    case 'reverse':
      state.rotation = quatMultiply(state.rotation, quatFromAxisAngle(UP, Math.PI));
      break;
    case 'push':
      stack.push(cloneState(state));
      break;
    case 'pop':
      if (stack.length > 0) {
        const restored = stack.pop()!;
        state.position = restored.position;
        state.rotation = restored.rotation;
        state.penDown = restored.penDown;
        currentLine = [{ ...state.position }];
        polylines.push(currentLine);
      }
      break;
    // 'noop': do nothing
  }
  return currentLine;
}

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

// Build default symbol endpoints with dropdowns
const symbolEndpoints: Record<string, any> = {};
let symOrder = 10;
for (const [sym, action] of Object.entries(DEFAULT_SYMBOLS)) {
  symbolEndpoints[sym] = {
    dataType: 'string', value: action,
    options: TURTLE_ACTIONS, optionLabels: TURTLE_LABELS,
    custom: true, group: 'Symbols', order: symOrder++,
  };
}

const turtleEndpoints = {
  ...params({
    preset: { dataType: 'string', options: TURTLE_PRESETS.map(p => p.name), value: TURTLE_PRESETS[0].name },
    sequence: { dataType: 'array', primary: true, label: 'Sequence' },
    angle: { value: 90, min: 0, max: 360, step: 1, label: 'Angle' },
    color: { dataType: 'color', value: { r: 0.231, g: 0.51, b: 0.965, a: 1 }, label: 'Color', group: 'Appearance' },
    bgColor: { dataType: 'color', value: { r: 0, g: 0, b: 0, a: 1 }, label: 'Background', group: 'Appearance' },
    lineWidth: { value: 1, min: 0.1, max: 20, step: 0.1, label: 'Line Width', group: 'Appearance' },
    ...symbolEndpoints,
    normalize: { dataType: 'boolean', value: true, label: 'Normalize', group: 'Bounding Box' },
    liveNormalize: { dataType: 'boolean', value: false, label: 'Live Normalize', group: 'Bounding Box' },
    bbWidth: { value: 1, min: 0.01, max: 100, step: 0.1, label: 'Width', group: 'Bounding Box' },
    bbHeight: { value: 1, min: 0.01, max: 100, step: 0.1, label: 'Height', group: 'Bounding Box' },
    bbDepth: { value: 1, min: 0.01, max: 100, step: 0.1, label: 'Depth', group: 'Bounding Box' },
  }),
  ...internals({
    polylines: { dataType: 'polylines', isOutput: true, allowInput: false, label: 'Polylines' },
    videoOut: { dataType: 'video', isOutput: true }
  }),
  ...events({
    action: { label: 'Action', isInput: true }
  }),
  ...triggers({
    reset: { label: 'Reset' }
  })
};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class TurtleNode extends BaseNode<typeof turtleEndpoints> {
  private state!: TurtleState;
  private stateStack!: TurtleState[];
  private livePolylines!: Vec3[][];
  private liveCurrentLine!: Vec3[];
  private renderer: PolylineRenderer;
  private unregisterRenderer: () => void;
  private cachedPolylines: Polylines = [];

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
    this.renderer = new PolylineRenderer();
    deps.endpointApi.registerRenderer(this.renderer);
    this.unregisterRenderer = deps.endpointApi.unregisterRenderer;
  }

  initialize(): void {
    super.initialize();
    this.resetLiveState();
    this.renderer.setColor(colorToHex(this.params.color.value));
    const seq = this.params.sequence.value;
    this.logTrace(`initialize: sequence=${seq ? `array[${seq.length}]` : 'empty'}`);
    if (seq && Array.isArray(seq) && seq.length > 0) {
      this.batchCompute();
    }
  }

  onParamChanged(_payload: Payload, endpointId: string): void {
    this.logTrace(`onParamChanged: ${endpointId}`);
    if (endpointId === 'liveNormalize' || endpointId === 'bgColor') return;
    if (endpointId === 'color' || endpointId === 'lineWidth') {
      if (endpointId === 'color') this.renderer.setColor(colorToHex(this.params.color.value));
      // Re-batch so the styled polyline data carries the new color/lineWidth
      this.batchCompute();
      return;
    }
    this.batchCompute();
  }

  onDisconnectedFrom(endpointId: string): void {
    if (endpointId === 'sequence') {
      this.renderer.setPolylines([]);
      this.internals.polylines.set([]);
    }
  }

  handleEvent(payload: Payload, endpointId: string, _source: IEndpoint): void {
    if (endpointId === 'action') {
      const symbol = String(payload.value);
      if (symbol) {
        const angleRad = this.params.angle.value * (Math.PI / 180);
        const symbolMap = this.deps.endpointApi.getCustomParams();
        this.liveCurrentLine = executeSymbol(
          symbol, this.state, this.stateStack,
          angleRad, 1,
          this.livePolylines, this.liveCurrentLine,
          symbolMap
        );
        this.emitLivePolylines();
      }
    }
  }

  handleTrigger(_payload: Payload, endpointId: string): void {
    if (endpointId === 'reset') {
      this.resetLiveState();
      this.emitLivePolylines();
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Batch mode
  // ─────────────────────────────────────────────────────────────

  private batchCompute(): void {
    const raw = this.params.sequence.value as string | string[] | undefined;
    const sequence = !raw ? raw
      : typeof raw === 'string' ? raw.split('')
      : raw.flatMap((s: any) => typeof s === 'string' && s.length > 1 ? s.split('') : [s]);
    this.logTrace(`batchCompute: sequence=${Array.isArray(sequence) ? `array[${sequence.length}]` : typeof sequence}, angle=${this.params.angle.value}`);
    if (!sequence || !Array.isArray(sequence) || sequence.length === 0) {
      this.cachedPolylines = [];
      this.renderer.setPolylines([]);
      this.internals.polylines.set([]);
      return;
    }

    const angleRad = this.params.angle.value * (Math.PI / 180);
    const stepSize = 1;
    const symbolMap = this.deps.endpointApi.getCustomParams();
    const state: TurtleState = {
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0, w: 1 },
      penDown: true
    };
    const stack: TurtleState[] = [];
    const polylines: Vec3[][] = [];
    let currentLine: Vec3[] = [{ ...state.position }];
    polylines.push(currentLine);

    for (const symbol of sequence) {
      if (typeof symbol !== 'string') {throw new Error('sequence should be made of strings')}
      currentLine = executeSymbol(symbol, state, stack, angleRad, stepSize, polylines, currentLine, symbolMap);
    }

    let result = polylines.filter(line => line.length >= 2);

    if (this.params.normalize.value && result.length > 0) {
      result = this.normalizePolylines(result);
    }

    this.logTrace(`batchCompute: produced ${result.length} polylines, total points=${result.reduce((n, l) => n + l.length, 0)}`);
    const color = colorToHex(this.params.color.value);
    const lineWidth = this.params.lineWidth.value;
    this.cachedPolylines = result.map(points => ({ points, color, lineWidth }));
    this.renderer.setPolylines(this.cachedPolylines);
    this.internals.polylines.set(this.cachedPolylines);
  }

  private normalizePolylines(polylines: Vec3[][]): Vec3[][] {
    // Find bounding box of all points
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const line of polylines) {
      for (const p of line) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
        if (p.z < minZ) minZ = p.z;
        if (p.z > maxZ) maxZ = p.z;
      }
    }

    const spanX = maxX - minX || 1;
    const spanY = maxY - minY || 1;
    const spanZ = maxZ - minZ || 1;
    const maxSpan = Math.max(spanX, spanY, spanZ);

    const bbW = this.params.bbWidth.value;
    const bbH = this.params.bbHeight.value;
    const bbD = this.params.bbDepth.value;
    const scale = Math.min(bbW, bbH, bbD) / maxSpan;

    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const cz = (minZ + maxZ) / 2;

    return polylines.map(line =>
      line.map(p => ({
        x: (p.x - cx) * scale,
        y: (p.y - cy) * scale,
        z: (p.z - cz) * scale
      }))
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Stepped mode (event-driven)
  // ─────────────────────────────────────────────────────────────

  private resetLiveState(): void {
    this.state = {
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0, w: 1 },
      penDown: true
    };
    this.stateStack = [];
    this.liveCurrentLine = [{ ...this.state.position }];
    this.livePolylines = [this.liveCurrentLine];
  }

  private emitLivePolylines(): void {
    let filtered = this.livePolylines.filter(line => line.length >= 2);
    if (this.params.liveNormalize.value && filtered.length > 0) {
      filtered = this.normalizePolylines(filtered);
    }
    const color = colorToHex(this.params.color.value);
    const lineWidth = this.params.lineWidth.value;
    this.cachedPolylines = filtered.map(points => ({ points, color, lineWidth }));
    this.renderer.setPolylines(this.cachedPolylines);
    this.internals.polylines.set(this.cachedPolylines);
  }

  /** Duck-typed accessor for polyline adapter */
  getPolylines(): Polylines {
    return this.cachedPolylines;
  }

  dispose(): void {
    this.unregisterRenderer();
    this.renderer.dispose();
    super.dispose();
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const turtleNodeDef: NodeDef<TurtleNode> = {
  type: 'Turtle',
  shortName: 'turtle',
  info: 'Stateful 3D turtle graphics.\nBatch: connect sequence for immediate output.\nStepped: receive symbols via action event (e.g. from PatternPlayer).\nAdd custom params to map symbols to actions.',
  endpoints: turtleEndpoints,
  allowAddParam: true,
  factoryPresets: TURTLE_PRESETS,
  ctor: TurtleNode
};
